import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, BookOpen } from "lucide-react";
import { ARTICLES } from "../../data/articles";
import usePageMeta from "../../lib/usePageMeta";

const CATEGORY_ORDER = [
  "Workplace Chaplaincy & Employee Care",
  "Leadership & Workplace Culture",
  "Grief, Crisis & Emotional Well-Being",
  "Stress, Burnout & Resilience",
  "Industry & Workforce Insights",
];

const ALL = "All Articles";

export default function ArticlesIndex() {
  usePageMeta({
    title: "Articles & Insights | California Corporate Chaplains",
    description:
      "Practical perspectives on workplace care, leadership, grief, employee well-being, and organizational chaplaincy — written for the people leading and supporting today's workforce.",
  });

  const [selected, setSelected] = useState(ALL);

  const filtered = useMemo(
    () => (selected === ALL ? ARTICLES : ARTICLES.filter((a) => a.category === selected)),
    [selected],
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20" data-testid="articles-index">
      <div className="max-w-3xl mb-10">
        <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">Articles &amp; Insights</p>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight text-slate-900 leading-[1.05]">
          Practical Insight for Caring for People at Work
        </h1>
        <p className="mt-5 text-lg text-slate-600 leading-relaxed">
          Thoughtful, practical perspectives for leaders and employees navigating grief, stress, caregiving, crisis, leadership pressure, workplace relationships, and the human realities employees carry with them to work.
        </p>
      </div>

      <div
        role="tablist"
        aria-label="Article categories"
        className="mb-10 flex flex-wrap gap-2"
        data-testid="articles-category-tabs"
      >
        {[ALL, ...CATEGORY_ORDER].map((cat) => {
          const active = selected === cat;
          const testKey = cat.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
          return (
            <button
              key={cat}
              type="button"
              role="tab"
              aria-selected={active}
              aria-pressed={active}
              data-testid={`articles-tab-${testKey}`}
              onClick={() => setSelected(cat)}
              className={
                "inline-flex items-center rounded-full border px-4 py-1.5 text-xs sm:text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4A6FA5] focus-visible:ring-offset-2 " +
                (active
                  ? "bg-[#4A6FA5] text-white border-[#4A6FA5]"
                  : "bg-white text-slate-700 border-slate-300 hover:border-[#4A6FA5] hover:text-[#4A6FA5]")
              }
            >
              {active && (
                <span aria-hidden="true" className="mr-1.5 h-1.5 w-1.5 rounded-full bg-white" />
              )}
              {cat}
            </button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="articles-empty">No articles in this category yet.</p>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="articles-grid">
          {filtered.map((a) => (
            <Link
              key={a.slug}
              to={`/resources/articles/${a.slug}`}
              data-testid={`article-card-${a.slug}`}
              className="group rounded-2xl border border-slate-200 bg-white p-6 hover:border-[#4A6FA5] hover:shadow-md transition-all flex flex-col"
            >
              <div className="flex items-center gap-2 mb-3">
                <BookOpen className="w-4 h-4 text-[#4A6FA5]" strokeWidth={1.75} />
                <span className="text-xs uppercase tracking-[0.16em] text-[#4A6FA5] font-semibold">{a.category}</span>
              </div>
              <h2 className="font-heading text-lg font-medium text-slate-900 leading-snug">{a.title}</h2>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed flex-1">{a.excerpt}</p>
              <div className="mt-5 flex items-center justify-between text-xs">
                <span className="inline-flex items-center gap-1.5 text-slate-500"><Clock className="w-3.5 h-3.5" /> {a.readMins} min read</span>
                <span className="inline-flex items-center gap-1 text-[#4A6FA5] font-medium group-hover:gap-2 transition-all">Read Article <ArrowRight className="w-3.5 h-3.5" /></span>
              </div>
            </Link>
          ))}
        </div>
      )}

      <p className="mt-12 text-sm text-slate-500 max-w-3xl leading-relaxed">
        Looking for downloadable briefs and toolkits instead? Visit the <Link to="/resources" className="text-[#4A6FA5] hover:text-[#3B597B] font-medium underline underline-offset-2">Guides &amp; Downloads library</Link>.
      </p>
    </div>
  );
}
